import React, { useEffect, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import toast from 'react-hot-toast'
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search, LayoutDashboard, CalendarDays, MessageCircle, Trophy, Globe,
  Bell, User, LogOut, Menu, X, ArrowRight, Sparkles, Video, Award,
  Sun, Moon, Shield,
} from 'lucide-react'
import { useAuthStore } from './store/authStore'
import { useNotificationStore } from './store/notificationStore'
import { useThemeStore } from './store/themeStore'
import { connectSocket, disconnectSocket } from './utils/socket'
import { useTranslation } from 'react-i18next'
// Critical-path pages — eager imports
import Login from './pages/Login'
import Register from './pages/Register'
import TermsOfService from './pages/TermsOfService'
import PrivacyPolicy from './pages/PrivacyPolicy'
import LandingPage from './pages/LandingPage'
// All other pages — lazy imports for code splitting
const Dashboard = React.lazy(() => import('./pages/Dashboard'))
const Explore = React.lazy(() => import('./pages/Explore'))
const Profile = React.lazy(() => import('./pages/Profile'))
const Sessions = React.lazy(() => import('./pages/Sessions'))
const Chat = React.lazy(() => import('./pages/Chat'))
const VideoCall = React.lazy(() => import('./pages/VideoCall'))
const Leaderboard = React.lazy(() => import('./pages/Leaderboard'))
const Community = React.lazy(() => import('./pages/Community'))
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'))
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'))
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'))
import AdminRoute from './components/AdminRoute'
import SearchModal from './components/SearchModal'
import Avatar from './components/Avatar'
import { ErrorBoundary } from './components/ErrorBoundary'
import SkipLink from './components/SkipLink'
import EmailVerificationBanner from './components/EmailVerificationBanner'
import TimezoneBanner from './components/TimezoneBanner'
import CookieConsentBanner from './components/CookieConsentBanner'
import './App.css'

// Full-screen loading spinner shown while lazy chunks load
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

// Preload module map — mirrors the lazy import paths above
const preloadMap = {
  '/dashboard': () => import('./pages/Dashboard'),
  '/explore': () => import('./pages/Explore'),
  '/profile': () => import('./pages/Profile'),
  '/sessions': () => import('./pages/Sessions'),
  '/chat': () => import('./pages/Chat'),
  '/leaderboard': () => import('./pages/Leaderboard'),
  '/community': () => import('./pages/Community'),
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'

/* ============================================================
   ANIMATION VARIANTS
   ============================================================ */
const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.25, ease: 'easeOut' } },
  exit: { opacity: 0, scale: 0.92, transition: { duration: 0.15 } },
}

const slideDown = {
  hidden: { opacity: 0, height: 0 },
  visible: { opacity: 1, height: 'auto', transition: { duration: 0.25, ease: 'easeOut' } },
  exit: { opacity: 0, height: 0, transition: { duration: 0.2 } },
}

/* ============================================================
   NOTIFICATION BELL
   ============================================================ */
function NotificationBell() {
  const { notifications, unreadCount, fetchNotifications, markAsRead, markAllAsRead } = useNotificationStore()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  // Keyboard navigation - close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        aria-label="Notifications"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls="notification-panel"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white dark:ring-gray-900" />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} aria-hidden="true" />
            <motion.div
              variants={scaleIn}
              initial="hidden"
              animate="visible"
              exit="exit"
              id="notification-panel"
              role="dialog"
              aria-labelledby="notification-title"
              className="absolute right-0 top-full mt-3 w-[340px] glass-card rounded-2xl shadow-float z-50 overflow-hidden origin-top-right"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                <h3 id="notification-title" className="font-bold text-gray-900 dark:text-white text-sm">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-5 py-10 text-center">
                    <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm text-gray-400 dark:text-gray-500">No notifications yet</p>
                  </div>
                ) : (
                  notifications.slice(0, 10).map((n) => (
                    <button
                      key={n._id}
                      onClick={() => { markAsRead(n._id); setIsOpen(false) }}
                      className={`w-full text-left px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-b border-gray-50 dark:border-gray-800/50 ${!n.read ? 'bg-primary-50/60 dark:bg-primary-950/30' : ''
                        }`}
                    >
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{n.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{n.message}</p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5">{new Date(n.createdAt).toLocaleString()}</p>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ============================================================
   THEME TOGGLE
   ============================================================ */
function ThemeToggle() {
  const { theme, toggleTheme } = useThemeStore()

  return (
    <button
      onClick={toggleTheme}
      className="p-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200"
      aria-label="Toggle theme"
    >
      <AnimatePresence mode="wait" initial={false}>
        {theme === 'light' ? (
          <motion.div
            key="sun"
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Moon className="w-5 h-5" />
          </motion.div>
        ) : (
          <motion.div
            key="moon"
            initial={{ rotate: 90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: -90, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Sun className="w-5 h-5" />
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  )
}

function LanguageToggle() {
  const { i18n } = useTranslation();

  return (
    <select
      value={i18n.language}
      onChange={(e) => i18n.changeLanguage(e.target.value)}
      className="p-1.5 ml-1 text-sm font-semibold text-gray-500 bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
    >
      <option value="en">EN</option>
      <option value="hi">HI</option>
      <option value="es">ES</option>
    </select>
  );
}

/* ============================================================
   NAVBAR
   ============================================================ */
function Navbar({ onSearchClick }) {
  const { t } = useTranslation()
  const { user, logout } = useAuthStore()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [completedCount, setCompletedCount] = useState(0)
  const location = useLocation()

  useEffect(() => {
    if (user?.token) {
      fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/sessions?status=completed`, {
        headers: { Authorization: `Bearer ${user.token}` }
      }).then(r => r.json()).then(data => setCompletedCount(data.length || 0)).catch(() => { });
    }
  }, [user])

  const handleLogout = () => {
    disconnectSocket()
    logout()
  }

  const baseNavLinks = [
    { to: '/explore', label: t('Explore'), icon: Search },
    { to: '/dashboard', label: t('Dashboard'), icon: LayoutDashboard },
    { to: '/sessions', label: t('Sessions'), icon: CalendarDays },
    { to: '/chat', label: t('Chat'), icon: MessageCircle },
  ]

  const advancedNavLinks = completedCount > 0 ? [
    { to: '/leaderboard', label: t('Rankings'), icon: Trophy },
    { to: '/community', label: t('Community'), icon: Globe },
  ] : []

  const navLinks = [...baseNavLinks, ...advancedNavLinks]

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/')

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-gray-200/60 dark:border-gray-800/60 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-extrabold bg-gradient-to-r from-primary-500 via-indigo-500 to-accent-500 bg-clip-text text-transparent">
              SkillSwap
            </Link>
          </div>

          {/* Desktop / Large Screen Nav */}
          <div className="hidden lg:flex items-center gap-1 xl:gap-1.5">
            {user ? (
              <>
                {navLinks.map((link) => {
                  const Icon = link.icon
                  const active = isActive(link.to)
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onMouseEnter={() => preloadMap[link.to]?.()}
                      className={`flex items-center gap-1.5 px-2.5 xl:px-3 py-2 text-xs xl:text-sm font-medium rounded-xl transition-all duration-200 whitespace-nowrap ${active
                        ? 'nav-active font-semibold'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" strokeWidth={active ? 2.5 : 2} />
                      {link.label}
                    </Link>
                  )
                })}

                {/* Admin Portal Button - ONLY rendered if authenticated user is admin */}
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    onMouseEnter={() => preloadMap['/admin']?.()}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl transition-all border ml-1 ${
                      isActive('/admin')
                        ? 'bg-purple-600 text-white border-purple-500 shadow-md shadow-purple-500/25'
                        : 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/40 hover:bg-purple-100 dark:hover:bg-purple-900/50 border-purple-200 dark:border-purple-800/80 shadow-xs'
                    }`}
                    title="Admin Dashboard"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    <span>Admin</span>
                  </Link>
                )}

                <button
                  onClick={onSearchClick}
                  className="flex items-center gap-2 px-2.5 xl:px-3 py-1.5 ml-1 text-sm text-gray-400 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all group shrink-0"
                  aria-label="Search"
                >
                  <Search className="w-4 h-4 group-hover:text-primary-500 transition-colors" />
                  <span className="hidden xl:inline">Search...</span>
                  <kbd className="hidden 2xl:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-gray-400">
                    <span className="text-[8px]">Ctrl</span> K
                  </kbd>
                </button>

                <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />

                <ThemeToggle />
                <LanguageToggle />
                <NotificationBell />

                <Link
                  to="/profile"
                  className="ml-1 transition-all duration-200 shrink-0"
                >
                  <Avatar src={user.avatar} name={user.name} size="sm" className="ring-2 ring-white dark:ring-gray-900 shadow-lg shadow-primary-500/20" />
                </Link>
                <button
                  onClick={handleLogout}
                  className="ml-1 p-2.5 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-xl transition-all duration-200"
                  aria-label="Logout"
                >
                  <LogOut className="w-4.5 h-4.5" />
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-5 mr-3">
                  <a href="/#features" className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                    {t('Features')}
                  </a>
                  <a href="/#how-it-works" className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                    {t('How It Works')}
                  </a>
                  <a href="/#testimonials" className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                    {t('Testimonials')}
                  </a>
                  <Link to="/explore" className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                    {t('Explore')}
                  </Link>
                </div>
                <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />
                <ThemeToggle />
                <LanguageToggle />
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {t('Sign In')}
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-accent-600 rounded-xl shadow-lg shadow-primary-500/20 hover:shadow-xl hover:shadow-primary-500/30 hover:-translate-y-0.5 transition-all duration-300"
                >
                  {t('Get Started')}
                </Link>
              </>
            )}
          </div>

          {/* Tablet & Mobile Nav Bar */}
          <div className="lg:hidden flex items-center gap-1.5">
            <button
              onClick={onSearchClick}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>
            <ThemeToggle />
            {user && (
              <>
                <NotificationBell />
                <Link to="/profile" className="ml-1 transition-all duration-200">
                  <Avatar src={user.avatar} name={user.name} size="sm" className="ring-2 ring-white dark:ring-gray-900 shadow" />
                </Link>
              </>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all"
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait" initial={false}>
                {mobileMenuOpen ? (
                  <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                    <X className="w-5 h-5" />
                  </motion.div>
                ) : (
                  <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                    <Menu className="w-5 h-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Tablet & Mobile Slide-Down Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              variants={slideDown}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="lg:hidden overflow-hidden"
            >
              <div className="pb-4 border-t border-gray-100 dark:border-gray-800 mt-2 pt-3 space-y-2">
                {user ? (
                  <>
                    {/* User Mini Profile Header */}
                    <div className="flex items-center justify-between px-4 py-3 mb-2 bg-gray-50 dark:bg-gray-800/60 rounded-2xl border border-gray-100 dark:border-gray-700/60">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar src={user.avatar} name={user.name} size="md" />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{user.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="px-2.5 py-1 text-xs font-semibold text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-950/60 rounded-lg">
                          {user.skillCredits || 0} Credits
                        </span>
                      </div>
                    </div>

                    {/* Navigation Links Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {navLinks.map((link) => {
                        const Icon = link.icon
                        const active = isActive(link.to)
                        return (
                          <Link
                            key={link.to}
                            to={link.to}
                            onMouseEnter={() => preloadMap[link.to]?.()}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all ${active
                              ? 'nav-active font-semibold'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                              }`}
                          >
                            <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
                            {link.label}
                          </Link>
                        )
                      })}
                      <Link
                        to="/profile"
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all ${isActive('/profile')
                          ? 'nav-active font-semibold'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                          }`}
                      >
                        <User className="w-5 h-5" />
                        Profile
                      </Link>
                    </div>

                    {/* Admin Portal Button (Mobile) - ONLY rendered if authenticated user is admin */}
                    {user?.role === 'admin' && (
                      <div className="pt-1">
                        <Link
                          to="/admin"
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 text-sm font-bold rounded-xl transition-all border ${
                            isActive('/admin')
                              ? 'bg-purple-600 text-white border-purple-500 shadow-md'
                              : 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/40 border-purple-200 dark:border-purple-800'
                          }`}
                        >
                          <Shield className="w-5 h-5" />
                          <span>Admin Dashboard</span>
                        </Link>
                      </div>
                    )}

                    <div className="border-t border-gray-100 dark:border-gray-800 my-2" />

                    <div className="flex items-center justify-between px-4 py-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Language</span>
                      <LanguageToggle />
                    </div>

                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false) }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-xl transition-all"
                    >
                      <LogOut className="w-5 h-5" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <a
                      href="/#features"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl"
                    >
                      {t('Features')}
                    </a>
                    <a
                      href="/#how-it-works"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl"
                    >
                      {t('How It Works')}
                    </a>
                    <a
                      href="/#testimonials"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl"
                    >
                      {t('Testimonials')}
                    </a>
                    <Link
                      to="/explore"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl"
                    >
                      {t('Explore Skills')}
                    </Link>
                    <div className="border-t border-gray-100 dark:border-gray-800 my-2" />
                    <div className="flex items-center justify-between px-4 py-2 mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Language</span>
                      <LanguageToggle />
                    </div>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block mx-4 px-5 py-3 text-center text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-accent-600 rounded-xl shadow-lg"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  )
}

/* ============================================================
   SOCKET MANAGER
   ============================================================ */
function SocketManager() {
  const { user } = useAuthStore()
  const { addNotification } = useNotificationStore()

  useEffect(() => {
    if (user) {
      const socket = connectSocket(user._id)

      socket.on('new-notification', (data) => {
        addNotification(data)
        // Show instant toast popup
        toast(data.title || 'New notification', {
          icon: '🔔',
          duration: 4000,
          style: {
            background: 'var(--toast-bg, #1e293b)',
            color: 'var(--toast-color, #f8fafc)',
            borderRadius: '12px',
            fontSize: '14px',
            border: '1px solid rgba(99,102,241,0.3)',
          },
        })
      })

      return () => {
        socket.off('new-notification')
      }
    } else {
      disconnectSocket()
    }
  }, [user, addNotification])

  return null
}

/* ============================================================
   MAIN LAYOUT & APP ROOT
   ============================================================ */
function App() {
  const { user } = useAuthStore()
  const { initTheme } = useThemeStore()
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  useEffect(() => {
    initTheme()
  }, [initTheme])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setIsSearchOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <div className="min-h-screen w-full overflow-x-hidden bg-transparent font-sans selection:bg-primary-100 dark:selection:bg-primary-900 selection:text-primary-900 dark:selection:text-primary-100 transition-colors duration-300 flex flex-col">
          <SkipLink />
          <Toaster position="top-right" toastOptions={{ className: 'dark:!bg-gray-800 dark:!text-white dark:!border-gray-700', style: { borderRadius: '12px', fontSize: '14px' } }} />
          <SocketManager />
          <EmailVerificationBanner />
          <TimezoneBanner />
          <CookieConsentBanner />
          <Navbar onSearchClick={() => setIsSearchOpen(true)} />
          <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
          <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" role="main">
            <React.Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/explore" element={user ? <Explore /> : <Navigate to="/login" />} />
                <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
                <Route path="/register" element={!user ? <Register /> : <Navigate to="/dashboard" />} />
                <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" />} />
                <Route path="/profile" element={user ? <Profile /> : <Navigate to="/login" />} />
                <Route path="/profile/:id" element={user ? <Profile /> : <Navigate to="/login" />} />
                <Route path="/sessions" element={user ? <Sessions /> : <Navigate to="/login" />} />
                <Route path="/chat" element={user ? <Chat /> : <Navigate to="/login" />} />
                <Route path="/chat/:id" element={user ? <Chat /> : <Navigate to="/login" />} />
                <Route path="/video/:roomId" element={user ? <VideoCall /> : <Navigate to="/login" />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/community" element={user ? <Community /> : <Navigate to="/login" />} />
                <Route path="/community/:id" element={user ? <Community /> : <Navigate to="/login" />} />
                {/* Legal pages */}
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                {/* New auth / account routes */}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                {/* Admin */}
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              </Routes>
            </React.Suspense>
          </main>
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
